import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { InventorySupplierSummaryResponse } from '@tabita/contracts';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { inventoryApi } from '@/services/inventory.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, EmptyState, Field, Spinner } from '@/components/ui';

/** Who the church buys from or rents equipment from. */
export function SuppliersListPage() {
  const { t } = useI18n();
  const { can } = useAuth();
  const [isCreating, setIsCreating] = useState(false);

  const query = useQuery({
    queryKey: ['inventory', 'suppliers'],
    queryFn: ({ signal }) => inventoryApi.listSuppliers({ limit: 100 }, signal),
  });

  const suppliers = query.data?.data ?? [];

  return (
    <div className="page">
      <Link to="/inventory" className="back-link">
        {t('common.actions.back')}
      </Link>

      <header className="page-header">
        <h1 className="page-title">{t('inventory.supplier.title')}</h1>
        {can('inventory.supplier.create') && (
          <button
            type="button"
            className="btn"
            data-variant="primary"
            onClick={() => setIsCreating(true)}
          >
            {t('inventory.supplier.create')}
          </button>
        )}
      </header>

      {query.isError && (
        <Alert tone="danger">
          {query.error instanceof ApiError
            ? t(`errors.${query.error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
            : t('errors.INTERNAL_ERROR')}
        </Alert>
      )}

      {query.isPending && <Spinner label={t('common.states.loading')} />}

      {query.isSuccess && suppliers.length === 0 && (
        <EmptyState title={t('inventory.supplier.empty')} hint={t('inventory.supplier.create')} />
      )}

      {suppliers.length > 0 && (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('inventory.supplier.singular')}</th>
                <th scope="col">{t('inventory.supplier.phone')}</th>
                <th scope="col">{t('inventory.supplier.email')}</th>
              </tr>
            </thead>
            <tbody>
              {suppliers.map((supplier) => (
                <SupplierRow key={supplier.id} supplier={supplier} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isCreating && (
        <SupplierForm
          onClose={() => setIsCreating(false)}
          onCreated={() => {
            setIsCreating(false);
            void query.refetch();
          }}
        />
      )}
    </div>
  );
}

function SupplierRow({ supplier }: { supplier: InventorySupplierSummaryResponse }) {
  return (
    <tr data-archived={supplier.archivedAt ? true : undefined}>
      <td>{supplier.name}</td>
      <td dir="ltr">{supplier.phone ?? <span className="cell-empty">—</span>}</td>
      <td dir="ltr">{supplier.email ?? <span className="cell-empty">—</span>}</td>
    </tr>
  );
}

function SupplierForm({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [form, setForm] = useState({ name: '', contactName: '', phone: '', email: '' });
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const mutation = useMutation({
    mutationFn: () =>
      inventoryApi.createSupplier({
        name: form.name.trim(),
        contactName: form.contactName.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
      }),
    onSuccess: onCreated,
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError
          ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
          : t('errors.INTERNAL_ERROR'),
      );
    },
  });

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      className="dialog"
      aria-labelledby="supplier-form-title"
    >
      <form
        className="dialog-body"
        noValidate
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          setFormError(null);
          mutation.mutate();
        }}
      >
        <h2 id="supplier-form-title">{t('inventory.supplier.create')}</h2>
        {formError && <Alert tone="danger">{formError}</Alert>}

        <Field
          label={t('inventory.supplier.name')}
          name="name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          required
          autoFocus
        />
        <Field
          label={t('inventory.supplier.contactName')}
          name="contactName"
          value={form.contactName}
          onChange={(e) => setForm({ ...form, contactName: e.target.value })}
        />
        <div className="form-grid">
          <Field
            label={t('inventory.supplier.phone')}
            name="phone"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            dir="ltr"
          />
          <Field
            label={t('inventory.supplier.email')}
            name="email"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            dir="ltr"
          />
        </div>

        <footer className="dialog-actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.actions.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={mutation.isPending}
            disabled={!form.name.trim()}
          >
            {t('common.actions.save')}
          </Button>
        </footer>
      </form>
    </dialog>
  );
}
